import { getDateForAttendee } from '@eventbrite/event-date-time';
import { getWindowObject } from '@eventbrite/feature-detection';
import { gettext } from '@eventbrite/i18n';
import { format, isValid, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { isEmpty } from 'lodash';
import { Dispatch } from 'redux';
import { setCollapsedSectionsHeight } from '../../actions/infiniteCalendarSeries';
import { CheckoutState } from '../../state';
import {
    AccordionSection,
    CalendarType,
    EventStatus,
} from '../../state/infiniteCalendarSeries';
import {
    ACCORDION_SECTION_CONTAINER_ID,
    ACCORDION_SECTION_HEADER_ID,
    COLLAPSED_SECTION_ANIMATION_DURATION_MS,
} from '../TimedEntrySeries/constants';
import { longDateFormat } from '../TimedEntrySeries/types';

const eventStatusAlertMessages: Record<EventStatus, string> = {
    [EventStatus.canceled]: gettext('This event has been cancelled').toString(),
    [EventStatus.postponed]: gettext(
        'This event has been postponed',
    ).toString(),
    [EventStatus.salesEnded]: gettext(
        'Ticket sales for this event have ended',
    ).toString(),
};

export const getLocalMidnightDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    // Month is 0-indexed in JavaScript Date constructor, so we need to subtract 1.
    return new Date(year, month - 1, day);
};

export const getLocalFormattedDateOrEmpty = (date: string, locale: string) => {
    const yyyyDdMmRegex = /^\d{4}-\d{2}-\d{2}$/;
    // If the date is in the format YYYY-MM-DD, we need to get the local midnight date.
    // Otherwise if the date also has a time, we need to get the local date using exact date.
    const parsedDate = yyyyDdMmRegex.test(date)
        ? getLocalMidnightDate(date)
        : new Date(date);
    if (!isValid(parsedDate)) {
        return '';
    }
    return parsedDate.toLocaleDateString(locale, longDateFormat);
};

export const getMonthAndYearFromDate = (utcDate: string) => {
    const date = new Date(utcDate);
    return { month: date.getMonth(), year: date.getFullYear() };
};

export const isAccordionSectionOpen = (
    activeSection: AccordionSection | null,
    currentSection: AccordionSection,
): boolean => {
    return activeSection === currentSection;
};

export const getDisplayDate = ({
    activeSection,
    calendarType,
    date,
    locale,
    isEventOnline,
    timezone,
}: {
    activeSection?: AccordionSection | null;
    calendarType?: CalendarType | null;
    date: string;
    locale: string;
    isEventOnline?: boolean;
    timezone: string;
}): string => {
    if (activeSection === AccordionSection.date || isEmpty(date)) {
        return '';
    }

    const hourFormat = 'hh:mm aa';
    const weekDayMonthDate = getLocalFormattedDateOrEmpty(date, locale);

    const time = isEventOnline
        ? format(new Date(date), hourFormat)
        : formatInTimeZone(parseISO(date), timezone, hourFormat);

    switch (calendarType) {
        case CalendarType.compactWithSession:
            return `${weekDayMonthDate}・${time}`;
        default:
            return weekDayMonthDate;
    }
};

const getContainersMargin = () => {
    const sectionContainers = document.querySelectorAll(
        `[data-section-container-id="${ACCORDION_SECTION_CONTAINER_ID}"]`,
    );
    return Array.from(sectionContainers).reduce((totalMargin, container) => {
        const computedStyle = window.getComputedStyle(container);
        const marginBottom = Number(
            computedStyle.marginBottom.replace(/px/g, ''),
        );
        const marginTop = Number(computedStyle.marginTop.replace(/px/g, ''));
        return totalMargin + marginBottom + marginTop;
    }, 0);
};

const getCollapsedSectionsHeaderHeight = () => {
    const collapsedSectionHeaders = document.querySelectorAll(
        `[data-section-id="${ACCORDION_SECTION_HEADER_ID}"]`,
    );
    return Array.from(collapsedSectionHeaders).reduce((totalHeight, header) => {
        const headerHeight = header.getBoundingClientRect().height;
        return totalHeight + headerHeight;
    }, 0);
};

export const calcCollapsedSectionsHeight = () => {
    if (getWindowObject('document') == null) {
        return 0;
    }
    const collapsedHeaderHeight = getCollapsedSectionsHeaderHeight();
    const containersMargin = getContainersMargin();
    return collapsedHeaderHeight + containersMargin;
};

export const getUnavailableEventAlert = (eventStatus: EventStatus) => {
    return eventStatusAlertMessages[eventStatus] || '';
};

export const hasDateAndTimeClubbedView = (calendarType?: CalendarType) => {
    return (
        calendarType === CalendarType.scrollCalendar ||
        calendarType === CalendarType.compactWithoutSession
    );
};

export const updateCollapsedSectionsHeight = (
    dispatch: Dispatch<CheckoutState>,
    calendarType?: CalendarType,
) => {
    const shouldDelayHeightCalculation =
        hasDateAndTimeClubbedView(calendarType);
    setTimeout(
        () => {
            dispatch(setCollapsedSectionsHeight(calcCollapsedSectionsHeight()));
        },
        shouldDelayHeightCalculation
            ? COLLAPSED_SECTION_ANIMATION_DURATION_MS
            : 0,
    );
};

export const getMonthAndYearForAttendee = (
    isOnlineEvent: boolean,
    utcDate: string,
    timezone: string,
) => {
    const date = getDateForAttendee({
        isOnlineEvent,
        dateAndTimezone: {
            utc: utcDate,
            timezone,
        },
    });
    return {
        month: date.month(),
        year: date.year(),
    };
};
